import './styles.scss'
import React, { useState, useEffect, useContext } from 'react'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'
import { useTranslation } from 'react-i18next'
import { Skeleton } from 'primereact/skeleton'
import DialogUIModal from 'Components/UIComponent/DialogUIModal'
import Button from 'Components/UIComponent/Button'
import { getLocalQuizStartSessionId, getLocalUserDetails, setLocalQuizStartSessionId, updateUserDetails } from 'Utils/LocalStorage_Handler'
import apiAdapterCoffeeWeb from 'Services/apiAdapter_CoffeeWeb'
import { CoffeeWebContext } from 'Context/CoffeeWebContext'
import { CoffeeWebWhiteLogoMask, coffeeCupLogo } from 'Assets/Icons'
import { ROUTE_STRINGS } from 'Utils/Constants'
import { useResizeHandler } from 'Utils/commonMethods'
import ErrorMessageModal from 'Components/UIComponent/ConfirmationModal'
import ComingSoonPopUp from 'Components/UIComponent/ComingSoonPopUp'
import CoffeeBeanConfetti from './Component/CoffeeBeanConfetti'

const CoffeeWebQuiz = (props) => {
  const { showConfirmation, showError, GameControls, setShowInfo, showInfo, selectedGames } = props
  const history = useHistory()
  const { t } = useTranslation()
  const { setLoading } = useContext(CoffeeWebContext)
  const userDetails = getLocalUserDetails()
  const keyString = process.env.REACT_APP_ENCRYPTION_KEY
  const ivString = process.env.REACT_APP_IV_STRING
  const { isWebView, isMobileView } = useResizeHandler()
  const quizSessionId = getLocalQuizStartSessionId()
  const [currentQuestion, setCurrentQuestion] = useState(0)
  const [totalQuestionCount, setTotalQuestionCount] = useState(0)
  const [progress, setProgress] = useState(0)
  const [userScore, setUserScore] = useState(0)
  const [quizQuestion, setQuizQuestion] = useState([])
  const [gameInstruction, setGameInstruction] = useState({})
  const welcomeDialog = gameInstruction?.welcomeDialog
  const [started, setStarted] = useState(false)
  const [visible, setVisible] = useState(false)
  const [time, setTime] = useState(null)
  const [selected, setSelected] = useState(null)
  const [timeLimit, setTimeLimit] = useState(null)
  const [correctAnswer, setCorrectAnswer] = useState(null)
  const [wrongAnswerCount, setWrongAnswerCount] = useState(null)
  const [quizLoading, setQuizLoading] = useState(true)
  const [hasAnswered, setHasAnswered] = useState(false)
  const [quizPlayLimitMessage, setQuizPlayLimitMessage] = useState(false)
  const [isRedeemModalOpen, setIsRedeemModalOpen] = useState(false)
  const [gameSessionId, setGameSessionId] = useState('')
  const [isRewardClaimed, setIsRewardClaimed] = useState(true)
  const [showContinueButton, setShowContinueButton] = useState(true)

  useEffect(() => {
    const handleContextmenu = (e) => {
      e.preventDefault()
    }

    document.addEventListener('contextmenu', handleContextmenu)

    return function cleanup() {
      document.removeEventListener('contextmenu', handleContextmenu)
    }
  }, [])

  useEffect(() => {
    if (!userDetails) {
      fetchGuestQuizQuestion()
    }

    if (userDetails) {
      fetchUserQuizScore()
    }
  }, [])

  useEffect(() => {
    if (userDetails && selectedGames?.gameId) {
      fetchGameInstruction()
    }
  }, [selectedGames])

  useEffect(() => {
    if (!started || timeLimit === 0) return

    let interval

    if (time > 0) {
      interval = setInterval(() => {
        setTime((prev) => {
          const newTime = prev - 1

          if (newTime >= 0) {
            setProgress(((timeLimit - newTime) / timeLimit) * 360)
          }

          if (newTime === 0 && !hasAnswered) {
            setHasAnswered(true)
            setSelected('skipped')
            postSelectedAnswer('skipped', quizQuestion)
          }

          return newTime >= 0 ? newTime : 0
        })
      }, 1000)
    }

    return () => clearInterval(interval)
  }, [time, timeLimit, started, hasAnswered])

  const stringToUint8Array = (str) => {
    const encoder = new TextEncoder()

    return encoder.encode(str)
  }

  const decryptData = async (encryptedData) => {
    try {
      const keyArray = stringToUint8Array(keyString)
      const ivArray = stringToUint8Array(ivString)

      const key = await window.crypto.subtle.importKey('raw', keyArray, { name: 'AES-CBC' }, false, ['decrypt'])

      const encryptedArrayBuffer = base64ToArrayBuffer(encryptedData)

      const decryptedArrayBuffer = await window.crypto.subtle.decrypt(
        {
          name: 'AES-CBC',
          iv: ivArray
        },
        key,
        encryptedArrayBuffer
      )

      const decoder = new TextDecoder()
      const decryptedData = decoder.decode(decryptedArrayBuffer)

      return decryptedData
    } catch (error) {
      console.error('failed:', error)
    }
  }

  const base64ToArrayBuffer = (base64) => {
    const binaryString = window.atob(base64)
    const length = binaryString.length
    const bytes = new Uint8Array(length)

    for (let i = 0; i < length; i++) {
      bytes[i] = binaryString.charCodeAt(i)
    }

    return bytes.buffer
  }

  const fetchUserQuizScore = async () => {
    try {
      const response = await apiAdapterCoffeeWeb.getUserQuizScore(userDetails?.id)
      const responseData = response?.data

      setTotalQuestionCount(responseData?.totalQuestions)
      setCurrentQuestion(responseData?.currentquestionNo)
      setUserScore(responseData?.totalScore)
      setWrongAnswerCount(responseData?.wrong)

      if (responseData) {
        fetchQuizQuestionById()
      }
    } catch (error) {
      console.error('failed:', error)
    }
  }

  const fetchGuestQuizQuestion = async () => {
    try {
      const response = await apiAdapterCoffeeWeb.guestQuizQuestion()
      const responseData = response?.data

      if (responseData?.timeLimit) {
        setTimeLimit(responseData.timeLimit)
        setTime(responseData.timeLimit)
      }

      setQuizQuestion(responseData)
      setQuizPlayLimitMessage(responseData?.message)
      setTotalQuestionCount(responseData?.totalQuestions)
      setCurrentQuestion(responseData?.currentquestionNo)
    } finally {
      setQuizLoading(false)
    }
  }

  const fetchGameInstruction = async () => {
    try {
      const response = await apiAdapterCoffeeWeb.getAllCoffeeQuizInstruction(selectedGames?.gameId)
      const responseData = response?.data

      setGameInstruction(responseData)
    } finally {
      setLoading(false)
    }
  }

  const fetchQuizSessionId = async () => {
    try {
      const response = await apiAdapterCoffeeWeb.getQuizSessionId(userDetails.id)
      const responseData = response?.data

      setGameSessionId(responseData)
      setLocalQuizStartSessionId(responseData)
    } finally {
      setLoading(false)
    }
  }

  const fetchQuizQuestionById = async () => {
    setQuizLoading(true)
    try {
      const response = await apiAdapterCoffeeWeb.getQuizQuestionById(userDetails?.id)
      const question = response.data

      const decryptedId = await decryptData(question?.updatedId)

      if (question?.timeLimit) {
        setTimeLimit(question.timeLimit)
        setTime(question.timeLimit)
      }

      setCorrectAnswer(decryptedId)

      setQuizQuestion(question)
      setTotalQuestionCount(question.totalQuestions)
      setCurrentQuestion(question.currentquestionNo)
      setQuizPlayLimitMessage(question.message)
    } finally {
      setLoading(false)
      setQuizLoading(false)
    }
  }

  const postSelectedAnswer = async (answer, questionId) => {
    setLoading(true)

    const questionData = {
      userId: userDetails?.id?.toString(),
      questionId: questionId?.id,
      selectedAnswer: answer,
      timeTaken: time
    }

    try {
      const response = await apiAdapterCoffeeWeb.postQuizAnswer(questionData)
      const quizStats = response?.data

      setUserScore(quizStats?.totalScore)
      setWrongAnswerCount(quizStats?.wrong)
      setIsRedeemModalOpen(quizStats?.optRedeem)
      setShowContinueButton(quizStats?.optRedeem)
    } catch {
      setLoading(false)
    } finally {
      setLoading(false)
    }
  }
  const submitRewardClaim = async () => {
    setLoading(true)

    let subscriptionId

    if (userScore >= Math.floor(totalQuestionCount / 2) && userScore < totalQuestionCount) {
      if (userDetails?.sub?.subType === 3) {
        subscriptionId = 3
      } else {
        subscriptionId = 2
      }
    } else {
      subscriptionId = 3
    }

    const data = {
      sessionId: gameSessionId || quizSessionId,
      userId: userDetails.id?.toString(),
      subId: subscriptionId,
      points: userScore
    }

    try {
      const response = await apiAdapterCoffeeWeb.redeemSubscriptionReward(data)
      const quizResult = response?.data

      if (quizResult) {
        setIsRewardClaimed(false)
        await updateUserDetails()
      }
    } catch {
      showError({
        modalCustomClassName: 'restart-error-modal',
        title: t('ERROR_TITLE'),
        message: `<p>${t('SOMETHING_WENT_WRONG')}</p><p>${t('TRY_AGAIN_MESSAGE')}</p>`,
        buttonLabel: t('OK')
      })
    } finally {
      setLoading(false)
    }
  }

  const restartQuiz = async () => {
    setLoading(true)

    const data = {
      userId: userDetails.id?.toString(),
      score: userScore
    }

    try {
      const response = await apiAdapterCoffeeWeb.restartQuizSession(data)
      const quizResult = response?.data

      if (quizResult) {
        resetQuiz()
        fetchQuizQuestionById()
      }
    } catch {
      setLoading(false)
      showError({
        modalCustomClassName: 'restart-error-modal',
        title: t('ERROR_TITLE'),
        message: `<p>${t('SOMETHING_WENT_WRONG')}</p><p>${t('TRY_AGAIN_MESSAGE')}</p>`,
        buttonLabel: t('OK')
      })
    } finally {
      setLoading(false)
    }
  }

  const handleQuizCompletion = async () => {
    setLoading(true)

    const data = {
      sessionId: gameSessionId || quizSessionId,
      status: 'completed',
      userid: userDetails.id?.toString()
    }

    try {
      const response = await apiAdapterCoffeeWeb.completeQuizSession(data)
      const quizResult = response?.data

      if (quizResult) {
        setVisible(true)
        setShowContinueButton(false)
        submitRewardClaim()
      }
    } catch {
      setLoading(false)
      showError({
        modalCustomClassName: 'restart-error-modal',
        title: t('ERROR_TITLE'),
        message: `<p>${t('SOMETHING_WENT_WRONG')}</p><p>${t('TRY_AGAIN_MESSAGE')}</p>`,
        buttonLabel: t('OK')
      })
    } finally {
      // setLoading(false)
    }
  }

  const handleQuizSessionComplete = async () => {
    setLoading(true)

    const data = {
      sessionId: gameSessionId || quizSessionId,
      status: 'completed',
      userid: userDetails.id?.toString()
    }

    try {
      const response = await apiAdapterCoffeeWeb.completeQuizSession(data)
      const quizResult = response?.data

      if (quizResult) {
        setVisible(false)
        resetQuiz()
        fetchQuizQuestionById()
      }
    } catch {
      setLoading(false)
      showError({
        modalCustomClassName: 'restart-error-modal',
        title: t('ERROR_TITLE'),
        message: `<p>${t('SOMETHING_WENT_WRONG')}</p><p>${t('TRY_AGAIN_MESSAGE')}</p>`,
        buttonLabel: t('OK')
      })
    } finally {
      setLoading(false)
    }
  }

  const handleStart = () => {
    if (!userDetails) {
      history.push({ pathname: ROUTE_STRINGS.login, state: { coffeeQuiz: true } })
    }

    if (started) return

    if (userDetails) {
      fetchQuizSessionId()
      setStarted(true)
      const limit = quizQuestion?.timeLimit || 10

      setTimeLimit(limit)
      setTime(limit)
      setProgress(0)
    }
  }

  const handleSelect = (optionId, questionId) => {
    if (userDetails) {
      if (hasAnswered) return
      setHasAnswered(true)
      setSelected(optionId)
      postSelectedAnswer(optionId, questionId)
    }
  }

  const handleNext = () => {
    setHasAnswered(false)
    setSelected(null)
    setProgress(0)
    fetchQuizQuestionById()
  }

  const handleCloseModal = () => {
    setShowInfo(false)
  }

  const getOptionStatus = (optionId) => {
    if (hasAnswered) {
      if (optionId === correctAnswer) return 'correct'
      if (selected === optionId && optionId !== correctAnswer) return 'wrong'
    }

    return ''
  }

  const openModal = () => {
    showConfirmation({
      modalCustomClassName: 'restart-game-modal',
      title: t('RESTART_GAME'),
      message: `<p>${t('RESTART_GAME_CONFIRMATION_MESSAGE')}</p><p>${t('RESTART_NOTE_MESSAGE')}</p>`,
      buttonLabel: t('RESTART'),
      dismissButtonLabel: t('CANCEL'),
      onConfirm: () => {
        restartQuiz()
      }
    })
  }

  const handleEndSession = () => {
    setVisible(true)
  }

  const handleClaimReward = () => {
    handleCloseRedeemConfirmation()
    setVisible(true)
  }

  const claimReward = () => {
    if (currentQuestion === totalQuestionCount) {
      submitRewardClaim()
    } else {
      handleQuizCompletion()
    }
  }

  const handleCloseRewardModal = () => {
    setVisible(false)
  }
  const handleCloseRedeemConfirmation = () => {
    setIsRedeemModalOpen(false)
  }

  const continuePlayingQuiz = () => {
    setVisible(false)
    setShowContinueButton(false)
  }

  const handleResumeGame = () => {
    if (currentQuestion === totalQuestionCount) {
      setVisible(false)
      resetQuiz()
      fetchQuizQuestionById()
    } else if (!isRewardClaimed) {
      setVisible(false)
      resetQuiz()
      fetchQuizQuestionById()
    } else {
      handleQuizSessionComplete()
    }
  }

  const resetQuiz = () => {
    setSelected(null)
    setProgress(0)
    setStarted(false)
    setTime(null)
    setTimeLimit(null)
    setCorrectAnswer(null)
    setQuizQuestion([])
    setCurrentQuestion(0)
    setTotalQuestionCount(0)
    setWrongAnswerCount(null)
    setUserScore(0)
    setHasAnswered(false)
    setGameSessionId('')
    setIsRewardClaimed(true)
    setShowContinueButton(false)
  }

  const renderButton = () => {
    if (!started) {
      return <Button buttonType={'CUSTOM'} buttonLabel={t('START')} variant={'hard-action'} onButtonClick={handleStart} />
    }

    if (currentQuestion === totalQuestionCount) {
      return <Button buttonType={'CUSTOM'} buttonLabel={t('FINISH')} variant={'hard-action'} onButtonClick={handleEndSession} disabled={selected === null && time > 0} />
    }

    return (
      <div className="quiz-controls-button">
        <Button buttonType={'CUSTOM'} buttonLabel={t('RESTART_QUIZ')} variant={'hard-action'} onButtonClick={openModal} />
        <Button buttonType={'CUSTOM'} buttonLabel={t('NEXT_QUESTION')} variant={'hard-action'} onButtonClick={handleNext} disabled={selected === null && time > 0} />
      </div>
    )
  }

  const instructionModalFooter = () => (
    <div className="instruction-modal-footer">
      <Button buttonType={'CUSTOM'} buttonLabel={t('GOT IT')} variant={'hard-action'} onButtonClick={handleCloseModal} />
    </div>
  )

  const getSubscriptionClassname = () => {
    if (userScore >= Math.floor(totalQuestionCount / 2) && userScore < totalQuestionCount) {
      if (userDetails?.sub?.subType === 3) {
        return 'platinum'
      }

      return 'gold'
    }

    if (userScore === totalQuestionCount) {
      return 'platinum'
    }

    return ''
  }

  const getRewardMessage = () => {
    if (userScore >= Math.floor(totalQuestionCount / 2) && userScore < totalQuestionCount) {
      if (userDetails?.sub?.subType === 3) {
        return ' Redeem a 15-day CoffeeWeb Platinum Subscription now!'
      }

      return ' Redeem a 30-day CoffeeWeb Gold Subscription now!'
    }

    if (userScore === totalQuestionCount) {
      return ' Redeem a 30-day CoffeeWeb Platinum Subscription now!'
    }

    return ''
  }

  const renderRewardModalBody = () => (
    <>
      <div className="quiz-result-wrapper">
        <div className="quiz-result-container">
          <div className="quiz-result-image">
            <img src={coffeeCupLogo} alt="coffee-cup" />
          </div>
          <div className="quiz-completion-message">{t('QUIZ_COMPLETION_MESSAGE')}</div>
          <div className="quiz-congratulations-message">{t('QUIZ_CONGRATULATIONS_MESSAGE')}</div>
        </div>

        <div className="quiz-results-content">
          <div className="result-block total">
            <div className="result-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24px" height="24px" viewBox="0 -960 960 960">
                <rect x="240" y="-880" width="640" height="640" fill="#50b3f6" rx="80" />

                <path d="M560-360q17 0 29.5-12.5T602-402q0-17-12.5-29.5T560-444q-17 0-29.5 12.5T518-402q0 17 12.5 29.5T560-360Zm-30-128h60q0-29 6-42.5t28-35.5q30-30 40-48.5t10-43.5q0-45-31.5-73.5T560-760q-41 0-71.5 23T446-676l54 22q9-25 24.5-37.5T560-704q24 0 39 13.5t15 36.5q0 14-8 26.5T578-596q-33 29-40.5 45.5T530-488Z" fill="white" />

                <path d="M160-80q-33 0-56.5-23.5T80-160v-560h80v560h560v80H160Zm160-720v480-480Z" fill="#50b3f6" />
              </svg>
            </div>
            <div className="result-value">
              <span className="total">{totalQuestionCount}</span>
              <span className="label-text">{t('TOTAL')}</span>
            </div>
          </div>

          <div className="result-block wrong">
            <div className="result-icon">
              <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 0 24 24" width="24px">
                <circle cx="12" cy="12" r="12" fill="#F44336" />

                <path d="M15.59 7.41 12 11 8.41 7.41 7 8.83 10.59 12 7 15.59 8.41 17 12 13.41 15.59 17 17 15.59 13.41 12 17 8.41z" fill="white" />
              </svg>
            </div>
            <div className="result-value">
              <span className="wrong">{wrongAnswerCount}</span>
              <span className="label-text">{t('WRONG')}</span>
            </div>
          </div>

          <div className="result-block correct">
            <div className="result-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24px" height="24px" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="12" fill="#4CAF50" />

                <path d="M16.97 7.97 10 14.94l-2.97-2.97-1.41 1.41L10 17.76l8.38-8.38-1.41-1.41z" fill="white" />
              </svg>
            </div>
            <div className="result-value">
              <span className="correct">{userScore}</span>
              <span className="label-text">{t('CORRECT')}</span>
            </div>
          </div>
        </div>

        {isRewardClaimed && (
          <div className="quiz-rewards-section">
            {userScore < Math.floor(totalQuestionCount / 2) ? <h3 className="rewards-received-title">{t('BETTER_LUCK')}</h3> : <h3 className="rewards-received-title">{t('REWARDS_RECEIVED')}</h3>}

            {(userScore >= Math.floor(totalQuestionCount / 2) && userScore < totalQuestionCount) || userScore === totalQuestionCount ? (
              <div className={`reward-item ${getSubscriptionClassname()}`}>
                <div className="reward-details">
                  <img src={CoffeeWebWhiteLogoMask} alt="reward-icon" className="reward-logo" />
                  <div>
                    <span className="reward-text"> {getRewardMessage()} </span>
                  </div>
                </div>
                <div className="claim-button">
                  <Button buttonType={'CUSTOM'} buttonLabel={t('CLAIM')} variant={'hard-action'} onButtonClick={claimReward} />
                </div>
              </div>
            ) : (
              <div>
                <p>{t('NEAR_MISS_MESSAGE')}</p>
                <p>{t('MOTIVATION_MESSAGE')}</p>
              </div>
            )}
          </div>
        )}
      </div>

      <CoffeeBeanConfetti trigger={visible} />
    </>
  )

  const rewardModalFooter = () => (
    <>
      <Button buttonType={'CUSTOM'} buttonLabel={t('PLAY_AGAIN')} variant={'hard-action'} onButtonClick={handleResumeGame} />
      {showContinueButton && <Button buttonType={'CUSTOM'} buttonLabel={t('CONTINUE_TO_PLAY')} variant={'hard-action'} onButtonClick={continuePlayingQuiz} />}
    </>
  )

  const renderRedeemConfirmation = () => (
    <>
      <div className="reward-message">
        {/* eslint-disable-next-line quotes */}
        {userDetails?.sub?.subType === 3 ? `You've won a Platinum subscription!` : `You've won a Gold subscription!`}
      </div>
      <div className="action-prompt">{t('ACTION_PROMPT')}</div>
    </>
  )

  const renderRedeemModalActions = () => (
    <div className="redeem-actions-footer">
      <Button buttonType={'CUSTOM'} buttonLabel={t('CONTINUE_PLAY')} variant={'soft-action'} onButtonClick={handleCloseRedeemConfirmation} />
      <Button buttonType={'CUSTOM'} buttonLabel={t('CLAIM')} variant={'hard-action'} onButtonClick={handleClaimReward} />
    </div>
  )

  const progressStats = [
    { label: 'Total Question', count: totalQuestionCount },
    { label: 'Current Question', count: currentQuestion },
    { label: 'Points Earned', count: userScore }
  ]

  const QuizClock = ({ progress, time }) => (
    <div className="clock-wrapper">
      <svg viewBox="0 -10 80 140" className="clock-svg">
        <g transform="translate(50,70)">
          <circle cx="0" cy="0" r="45" stroke="#50b3f6" strokeWidth="6" fill="none" />
          <circle cx="0" cy="0" r="45" stroke="#a0a0a0" strokeWidth="6" fill="none" strokeDasharray="283" strokeDashoffset={283 - (progress / 360) * 283} strokeLinecap="round" className="progress-ring" />
          {Array.from({ length: 12 }).map((_, i) => (
            <line key={i} x1="0" y1="-32" x2="0" y2="-38" stroke="#50b3f6" strokeWidth="3" transform={`rotate(${i * 30})`} />
          ))}

          {/* Clock knobs  */}
          <rect x={-5} y={-55} width={8} height={8} rx={0} fill="#50b3f6" />
          <rect x={-11} y={-60} width={20} height={9} rx={3} fill="#50b3f6" />
          <g transform="rotate(-35)">
            <rect x={-5} y={-53} width={8} height={6} rx={0} fill="#50b3f6" />
            <rect x={-11} y={-60} width={20} height={8} rx={3} fill="#50b3f6" />
          </g>
          <g transform="rotate(35)">
            <rect x={-5} y={-53} width={8} height={6} rx={0} fill="#50b3f6" />
            <rect x={-11} y={-60} width={20} height={8} rx={3} fill="#50b3f6" />
          </g>

          <text x="0" y="4" textAnchor="middle" fontSize="24" fill="#50b3f6" dominantBaseline="middle" className="time-text">
            {Math.max(0, time)}
          </text>
        </g>
      </svg>
    </div>
  )

  const StatsTable = ({ progressStats }) => (
    <div className="count-container">
      <table className="count-table">
        <tbody>
          {progressStats?.map((item, index) => (
            <tr key={index}>
              <td className="count-label">{item?.label}</td>
              <td>
                <div className="count-box">{item?.count}</div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )

  const renderWelcomeDialog = () => {
    if (!welcomeDialog) return null

    const { welcomeTitle, welcomeSubtitle, goldReward, platinumReward, existingSubscriberTitle, existingSubscriberDescription } = welcomeDialog

    return (
      <div className="quiz-welcome-dialog">
        <h2 className="welcome-title">{welcomeTitle}</h2>
        <p className="welcome-subtitle">{welcomeSubtitle}</p>

        <div className="winning-rewards-btn">Winning Rewards</div>

        {/* Gold Reward */}
        <div className="reward-card gold">
          <h3>{goldReward?.title}</h3>
          <p>{goldReward?.description}</p>
        </div>

        {/* Platinum Reward */}
        <div className="reward-card platinum">
          <h3>{platinumReward?.title}</h3>
          <p>{platinumReward?.description}</p>
        </div>

        <div className="existing-subscriber">
          <h4>{existingSubscriberTitle}</h4>
          <p>{existingSubscriberDescription}</p>
        </div>
      </div>
    )
  }

  return (
    <>
      {!userDetails || userDetails?.isCoffeeGamesAvailable ? (
        <div className="coffee-quiz-container">
          {quizLoading ? (
            <div className="skeleton-container">
              <div className="top-skeletons">
                <Skeleton className="custom-skeleton" />
                <Skeleton className="custom-skeleton" />
              </div>

              <>
                <div className="main-skeleton">
                  <Skeleton className="question-skeleton" />
                </div>

                <div className="options-grid">
                  {[1, 2, 3, 4].map((_, index) => (
                    <div key={index} className="option-item">
                      <Skeleton shape="circle" className="option-icon" />
                      <Skeleton className="option-text" />
                    </div>
                  ))}
                </div>

                <div className="bottom-button">
                  <Skeleton className="button-skeleton" />
                </div>
              </>
            </div>
          ) : (
            <>
              <div className="quiz-wrapper">
                <>
                  <div className="quiz-header">
                    {isWebView && (
                      <>
                        <StatsTable progressStats={progressStats} />
                        <GameControls />
                        <QuizClock progress={progress} time={time} />
                      </>
                    )}

                    {isMobileView && (
                      <>
                        <GameControls />
                        <div className="quiz-stats-wrapper">
                          <StatsTable progressStats={progressStats} />
                          <QuizClock progress={progress} time={time} />
                        </div>
                      </>
                    )}
                  </div>
                  {quizPlayLimitMessage !== null && quizPlayLimitMessage !== '' ? (
                    <div className="daily-limit-message">
                      <div className="icon-wrapper">
                        <i className="pi pi-exclamation-triangle warning-icon" />
                      </div>
                      <div className="message-content">
                        <p>{quizPlayLimitMessage}</p>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="question-section">
                        {quizQuestion?.question && (
                          <h3 className="question">
                            {currentQuestion}. {quizQuestion?.question}
                          </h3>
                        )}

                        <div className="options">
                          {quizQuestion?.options?.map((opt) => (
                            <label key={opt} className={`option ${selected !== null ? getOptionStatus(opt) : ''}`}>
                              <div className={`custom-radio ${getOptionStatus(opt)}`} onClick={handleStart}>
                                <input type="radio" name={`q-${currentQuestion}`} checked={selected === opt} onChange={() => handleSelect(opt, quizQuestion)} />

                                {selected !== null && (
                                  <span className="radio-icon">
                                    {(() => {
                                      if (opt === correctAnswer)
                                        return (
                                          <svg xmlns="http://www.w3.org/2000/svg" width="24px" height="24px" viewBox="0 0 24 24">
                                            <circle cx="12" cy="12" r="12" fill="#4CAF50" />

                                            <path d="M16.97 7.97 10 14.94l-2.97-2.97-1.41 1.41L10 17.76l8.38-8.38-1.41-1.41z" fill="white" />
                                          </svg>
                                        )
                                      if (selected === opt && opt !== correctAnswer)
                                        return (
                                          <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 0 24 24" width="24px">
                                            <circle cx="12" cy="12" r="12" fill="#F44336" />

                                            <path d="M15.59 7.41 12 11 8.41 7.41 7 8.83 10.59 12 7 15.59 8.41 17 12 13.41 15.59 17 17 15.59 13.41 12 17 8.41z" fill="white" />
                                          </svg>
                                        )

                                      return null
                                    })()}
                                  </span>
                                )}
                              </div>
                              <span>{opt}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      <div className="button-section">{renderButton()}</div>
                    </>
                  )}
                </>
              </div>
            </>
          )}

          <DialogUIModal size={'md'} titlePosition="center" showModal={showInfo} handleClose={handleCloseModal} bodyContent={renderWelcomeDialog} footerContent={instructionModalFooter} position="centre" customClassName="instruction-modal" />
          <DialogUIModal title={t('QUIZ_RESULT')} size={'md'} titlePosition="center" showModal={visible} handleClose={handleCloseRewardModal} bodyContent={renderRewardModalBody} footerContent={rewardModalFooter} position="centre" customClassName="result-modal" />
          <DialogUIModal title={t('CONGRATULATIONS')} size={'md'} titlePosition="center" showModal={isRedeemModalOpen} handleClose={handleCloseRedeemConfirmation} bodyContent={renderRedeemConfirmation} footerContent={renderRedeemModalActions} position="centre" customClassName="redeem-confirmation-modal" />
        </div>
      ) : (
        <ComingSoonPopUp />
      )}
    </>
  )
}

export default ErrorMessageModal(CoffeeWebQuiz)
